import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { buildLoginRedirect, hasLiveSession } from '@/utils/auth-session';
import { clearAccessTokenOnly } from '@/utils/auth-session';
import { readAccessToken, readRefreshToken } from '@/utils/auth-token-store';
import { refreshNow } from '@/utils/auth-refresh';
import { FullScreenLoader } from '@/components/ui/full-screen-loader';
const PrivateRoute = ({ children }) => {
    const token = readAccessToken();
    const location = useLocation();
    const live = hasLiveSession(token);
    // An expired access token is not necessarily a dead session: a refresh token
    // may still renew it. Previously this redirected straight to login, which is
    // what logged people out mid-session (and, on a deep link, produced a 404).
    // Try renewal once before giving up.
    const canRenew = !live && !!readRefreshToken();
    const [renewing, setRenewing] = useState(canRenew);
    useEffect(() => {
        if (!canRenew)
            return;
        let active = true;
        refreshNow().finally(() => {
            if (active)
                setRenewing(false);
        });
        return () => {
            active = false;
        };
        // Deliberately keyed on the route, not on `canRenew`: one attempt per
        // navigation, and no re-run when the token changes underneath us.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
    // Hold the route rather than flashing the login screen while renewal is in
    // flight — a redirect here would lose the page the user asked for.
    if (renewing)
        return _jsx(FullScreenLoader, {});
    // VA-27781: presence is not enough. This used to be `!!token`, and an EXPIRED
    // token is still a non-empty string — so the guard passed, the protected page
    // mounted, and every request behind it failed 401 while the user sat looking at
    // empty widgets with no explanation and no way back to login.
    //
    // A token whose expiry cannot be read is left alone rather than rejected — see
    // `getJwtExpiry`. Logging everyone out because a token shape changed would be
    // worse than the bug being fixed; the 401 interceptor backstops that case.
    if (!hasLiveSession(readAccessToken())) {
        // Retire only the dead ACCESS token and keep the refresh token, so a later
        // attempt (the fetch-level 401 backstop, or the next navigation) can still
        // renew. `clearSessionStorage` would delete the refresh token as well, and
        // dropping the single credential that can recover the session is what
        // turned an expired token into a forced re-login in the first place. The
        // full clear belongs to an explicit logout, not to an expiry.
        if (token)
            clearAccessTokenOnly();
        // Preserve where they were headed (e.g. the report link from the
        // interview-completion email) as `?next=`, so they return there after logging
        // in instead of being dumped on the dashboard.
        return (_jsx(Navigate, { to: buildLoginRedirect(location.pathname, location.search, location.hash), replace: true }));
    }
    return children; // else render the protected component
};
export default PrivateRoute;
